import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import ItemService from "../../apis/ItemService";
import PreviewIcon from "@mui/icons-material/Preview";
import ItemPreviewDialog from "./ItemPreviewDialog";

const TYPE_OPTIONS = [
  { value: "MCMA", label: "MCMA" },
  { value: "Markup", label: "Markup" },
  { value: "Match", label: "Match" },
  { value: "Iraab", label: "Iraab" },
  { value: "MFB", label: "MFB" },
  { value: "ReArrange", label: "ReArrange" },
  { value: "Ranking", label: "Ranking" },
];

const API_TYPE_MAP = {
  mcma: "MCMA",
  mfb: "MFB",
  match: "Match",
  iraab: "Iraab",
  arrange: "ReArrange",
  ranking: "Ranking",
  markup: "Markup",
};

const TYPE_KEY_MAP = Object.entries(API_TYPE_MAP).reduce(
  (acc, [key, value]) => {
    acc[value] = key;
    return acc;
  },
  {}
);

const getPlainText = (html) =>
  html
    ? html
        .replace(/<[^>]+>/g, " ")
        .replace(/\s+/g, " ")
        .trim()
    : "";

const resolveItemFromResponse = (response) => {
  if (!response) return null;
  if (response.item) return response.item;
  if (Array.isArray(response.items)) return response.items[0] || null;
  return response;
};

const normaliseItemTypeLabel = (type) =>
  type ? String(type).trim().toUpperCase() : "";

const resolveItemName = (item) => {
  if (typeof item?.Name === "string" && item.Name.trim())
    return item.Name.trim();
  if (typeof item?.name === "string" && item.name.trim())
    return item.name.trim();
  const plainTitle = getPlainText(item?.Title);
  return plainTitle || "Unnamed item";
};

const INITIAL_PREVIEW_STATE = {
  open: false,
  loading: false,
  error: null,
  type: "",
  name: "",
  content: null,
  unavailableMessage: null,
};

const ItemLibrary = ({ currentType }) => {
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedType, setSelectedType] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [previewState, setPreviewState] = useState(INITIAL_PREVIEW_STATE);
  const navigate = useNavigate();

  const resolveTypeKey = (typeLabel) => {
    if (!typeLabel) return undefined;
    const normalizedLabel = String(typeLabel).toUpperCase();
    return TYPE_KEY_MAP[typeLabel] || TYPE_KEY_MAP[normalizedLabel];
  };

  const handleRowNavigation = (item) => {
    if (!item?.id) return;
    const rowTypeKey =
      resolveTypeKey(item.Type) ||
      resolveTypeKey(selectedType) ||
      currentType?.key;
    if (!rowTypeKey) return;
    navigate(`/authoring/items/${rowTypeKey}/item/${item.id}`);
  };

  const summaryLabel = useMemo(() => {
    const matchedOption = TYPE_OPTIONS.find(
      (option) => option.value === selectedType
    );
    return matchedOption
      ? `${matchedOption.label} templates`
      : "Select a template type";
  }, [selectedType]);

  useEffect(() => {
    const mappedType = API_TYPE_MAP[currentType?.key];
    setSelectedType(mappedType ?? "");
  }, [currentType]);

  useEffect(() => {
    let isSubscribed = true;
    const loadItems = async () => {
      if (!selectedType) {
        if (isSubscribed) {
          setItems([]);
          setTotalCount(0);
          setError(null);
          setLoading(false);
        }
        return;
      }

      if (!ItemService.isConfigured()) {
        setError("Items API URL is not configured.");
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const data = await ItemService.fetchItems(selectedType);
        if (!isSubscribed) return;
        setItems(Array.isArray(data?.items) ? data.items : []);
        setTotalCount(
          typeof data?.totalCount === "number"
            ? data.totalCount
            : Array.isArray(data?.items)
            ? data.items.length
            : 0
        );
      } catch (err) {
        if (!isSubscribed) return;
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Unable to load items."
        );
        setItems([]);
        setTotalCount(0);
      } finally {
        if (isSubscribed) setLoading(false);
      }
    };
    loadItems();
    return () => {
      isSubscribed = false;
    };
  }, [selectedType]);

  const handlePreviewClose = () => {
    setPreviewState(INITIAL_PREVIEW_STATE);
  };

  const handlePreviewClick = async (event, item) => {
    event.stopPropagation();
    event.preventDefault();
    const displayType = item.Type || selectedType || "";
    const normalisedType = normaliseItemTypeLabel(displayType);
    const itemName = resolveItemName(item);

    setPreviewState({
      open: true,
      loading: true,
      type: normalisedType,
      name: itemName,
      error: null,
      content: null,
    });

    try {
      const response = await ItemService.fetchItemById(item.id);
      const resolvedItem = resolveItemFromResponse(response);
      setPreviewState((prev) => ({
        ...prev,
        loading: false,
        content: resolvedItem,
      }));
    } catch (err) {
      setPreviewState((prev) => ({
        ...prev,
        loading: false,
        error:
          err?.response?.data?.message ||
          err?.message ||
          "Unable to load item preview.",
      }));
    }
  };

  return (
    <Paper elevation={1} sx={{ p: { xs: 2, md: 3 } }}>
      <Stack spacing={3}>
        {/* Header */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }} gutterBottom>
              Item Library
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                label={summaryLabel}
                color={selectedType ? "primary" : "default"}
                variant={selectedType ? "outlined" : "filled"}
              />
              <Typography variant="body2" color="text.secondary">
                {selectedType
                  ? `${totalCount} items available`
                  : "Choose a type to load templates"}
              </Typography>
            </Stack>
          </Box>

          <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 220 } }}>
            <InputLabel id="item-type-filter-label">Filter by type</InputLabel>
            <Select
              labelId="item-type-filter-label"
              id="item-type-filter"
              label="Filter by type"
              displayEmpty
              value={selectedType}
              onChange={(event) => setSelectedType(event.target.value)}
            >
              <MenuItem value="" disabled>
                Select a type
              </MenuItem>
              {TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        {loading && <LinearProgress />}
        {error && !loading && <Alert severity="error">{error}</Alert>}
        {!selectedType && !loading && !error && (
          <Alert severity="info">Select an item type to view templates.</Alert>
        )}
        {!loading && !error && selectedType && items.length === 0 && (
          <Alert severity="info">No items found for this selection.</Alert>
        )}

        {/* Modern List Layout */}
        {!error && selectedType && items.length > 0 && (
          <Stack spacing={1.5}>
            {items.map((item) => (
              <Paper
                key={item.id || item.Name}
                elevation={0}
                onClick={() => handleRowNavigation(item)}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  transition: "all 0.2s ease",
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "action.hover",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                  },
                }}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  justifyContent="space-between"
                  spacing={1.5}
                >
                  {/* Left Section */}
                  <Stack spacing={0.5}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Tooltip title="Preview item">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={(event) => handlePreviewClick(event, item)}
                          aria-label={`Preview ${resolveItemName(item)}`}
                        >
                          <PreviewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Typography
                        variant="subtitle1"
                        fontWeight={600}
                        sx={{ wordBreak: "break-word" }}
                      >
                        {item.Name || "Unnamed"}
                      </Typography>

                      <Chip
                        size="small"
                        label={item.Type || "Unknown"}
                        color="secondary"
                        variant="outlined"
                      />
                    </Stack>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        maxWidth: 700,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                      }}
                    >
                      {getPlainText(item.Title) || "—"}
                    </Typography>
                  </Stack>

                  {/* Right Section */}
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={3}
                    sx={{ minWidth: 200 }}
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ textAlign: "right" }}
                    >
                      Points:{" "}
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.primary"
                        fontWeight={600}
                      >
                        {item.points ?? "—"}
                      </Typography>
                    </Typography>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ whiteSpace: "nowrap" }}
                    >
                      {item.dateUpdated
                        ? new Date(item.dateUpdated).toLocaleString()
                        : "—"}
                    </Typography>
                  </Stack>
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}
      </Stack>

      <ItemPreviewDialog
        open={previewState.open}
        onClose={handlePreviewClose}
        type={previewState.type}
        name={previewState.name}
        loading={previewState.loading}
        error={previewState.error}
        content={previewState.content}
        unavailableMessage={previewState.unavailableMessage}
      />
    </Paper>
  );
};

export default ItemLibrary;
